import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Dropdown,
  DropdownList,
  IconButton,
  Title,
  TrevorIcon,
  DropdownItem,
  DropdownItemWithConfirmation,
} from '@trevor/ui-components';
import {
  MaybeSavedQueryMeta,
  SavedQuery as SavedQueryT,
} from '@/views/SavedQueries/types';
import { safeOnClick } from '@/util/helpers/event-helpers';
import { useDatasourceId } from '@/hooks';
import { TrevorLink } from '@/components/TrevorLink';
import { useAppDispatch } from '@/store/hooks';
import { setLastUpdateMethod } from '@/features/query-builder-steps/queryBuilderStepsSlice';
import { LAST_UPDATE_METHOD } from '@/features/query-builder-steps/constants';
import { useModal } from '@/features/modals/modalSlice';
import { ShareModal } from '@/features/share-modal';
import { SeriesIdContextProvider } from '@/context/SeriesIdContext';
import {
  SHARE_OPTION,
  TShareOption,
} from '@/features/share-modal/ShareModal.constants';
import { reset } from '@/features/query-builder-steps-undo-redo';
import { ConnectedToolsButtons, Info } from './subcomponents';

const Container = styled.div`
  border-bottom: 1px solid #e8e8e8;
  display: flex;

  &:hover {
    background: #fafafa;
    cursor: pointer;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 20px;

  ${Title} {
    margin-bottom: 8px;
  }
`;

const ControlsContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  padding: 24px;
`;

export type SavedQueryProps = {
  savedQuery: SavedQueryT;
  meta: MaybeSavedQueryMeta;
  isFavourite: boolean;
  dashboardsById: Record<string, { name: F0<string> }>;
  onToggleFavourite: F0;
  onEdit: F0;
  onDuplicate: F0;
  onDelete: F0;
};

export const SavedQuery: React.FC<SavedQueryProps> = ({
  savedQuery,
  isFavourite,
  meta,
  dashboardsById,
  onToggleFavourite,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const datasourceId = useDatasourceId();
  const [isModalOpen, handleModalOpen] = useModal();
  const [shareOption, setShareOption] = React.useState<TShareOption>(
    SHARE_OPTION.download,
  );

  const savedQueryLink = `/datasources/${datasourceId}/saved-queries/${savedQuery.series()}`;

  const handleNavigateToDashboard = (dashboardId: string) =>
    navigate(`../dashboards/${dashboardId}`);

  const handleClick = () => {
    dispatch(setLastUpdateMethod(LAST_UPDATE_METHOD.backendSync));
    dispatch(reset());
  };

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    setShareOption(SHARE_OPTION.download);
    handleModalOpen();
  };

  const handleConnectedShare = (connectedShareOption: TShareOption) => () => {
    setShareOption(connectedShareOption);
    handleModalOpen();
  };

  return (
    <SeriesIdContextProvider seriesId={savedQuery.series()}>
      {isModalOpen && <ShareModal initialShareOption={shareOption} />}
      <TrevorLink to={savedQueryLink} onClick={handleClick}>
        <Container>
          <InfoContainer>
            <Info savedQuery={savedQuery} meta={meta} />
            {meta && (
              <ConnectedToolsButtons
                meta={meta}
                onNavigateToDashboard={handleNavigateToDashboard}
                onShare={handleConnectedShare}
                dashboardsById={dashboardsById}
              />
            )}
          </InfoContainer>
          <ControlsContainer>
            <IconButton
              icon={<TrevorIcon name={isFavourite ? 'star-filled' : 'star'} />}
              tooltip="Star query"
              onClick={safeOnClick(onToggleFavourite)}
            />
            <IconButton
              icon={<TrevorIcon name="share" $color="#888" size="sm" />}
              tooltip="Share / export"
              onClick={handleShare}
            />
            <Dropdown>
              <Dropdown.Trigger>
                <IconButton
                  onClick={(e: React.MouseEvent) => e.preventDefault()}
                  icon={
                    <TrevorIcon name="menu-vertical" $color="#888" size="sm" />
                  }
                />
              </Dropdown.Trigger>
              <Dropdown.Content top={37}>
                <DropdownList maxHeight={500}>
                  <DropdownItem onClick={onEdit}>Edit</DropdownItem>
                  <DropdownItem onClick={onDuplicate}>Duplicate</DropdownItem>
                  <DropdownItemWithConfirmation onConfirm={onDelete}>
                    Delete query
                  </DropdownItemWithConfirmation>
                </DropdownList>
              </Dropdown.Content>
            </Dropdown>
          </ControlsContainer>
        </Container>
      </TrevorLink>
    </SeriesIdContextProvider>
  );
};
